/**
 * TasksDueSoon - displays tasks that are due soon
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TaskDueSoon } from '@services/api';

interface TasksDueSoonProps {
  tasks: TaskDueSoon[];
  isLoading?: boolean;
}

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
  high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
  medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
  low: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
  none: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200',
};

export function TasksDueSoon({ tasks, isLoading }: TasksDueSoonProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">{t('dashboard.dueSoon')}</h3>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-lg bg-neutral-50 dark:bg-neutral-700">
              <div className="w-4 h-4 rounded bg-neutral-200 dark:bg-neutral-600" />
              <div className="flex-1">
                <div className="h-4 bg-neutral-200 dark:bg-neutral-600 rounded w-3/4 mb-1" />
                <div className="h-3 bg-neutral-200 dark:bg-neutral-600 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getDueLabel = (days: number): { text: string; color: string } => {
    if (days < 0) {
      return {
        text: t('dashboard.daysOverdue_other', { count: Math.abs(days) }),
        color: 'text-red-600 dark:text-red-400',
      };
    }
    if (days === 0) {
      return { text: t('dashboard.dueTodayLabel'), color: 'text-orange-600 dark:text-orange-400' };
    }
    if (days === 1) {
      return { text: t('dashboard.dueTomorrow'), color: 'text-yellow-600 dark:text-yellow-400' };
    }
    return { text: t('dashboard.dueInDays', { count: days }), color: 'text-neutral-600 dark:text-neutral-400' };
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">{t('dashboard.dueSoon')}</h3>
      {tasks.length === 0 ? (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-neutral-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">{t('dashboard.noTasksDueSoon')}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {tasks.map((task) => {
            const dueInfo = getDueLabel(task.daysUntilDue);
            const priorityClass = priorityColors[task.priority ?? 'none'] ?? priorityColors.none;

            return (
              <li
                key={task.id}
                onClick={() => navigate(`/boards/${task.boardId}`)}
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 cursor-pointer transition-colors"
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    task.daysUntilDue < 0
                      ? 'bg-red-500'
                      : task.daysUntilDue === 0
                        ? 'bg-orange-500'
                        : 'bg-yellow-500'
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                    {task.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">{task.projectName}</span>
                    <span className={`text-xs ${dueInfo.color}`}>{dueInfo.text}</span>
                  </div>
                </div>
                {task.priority && (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${priorityClass}`}
                  >
                    {task.priority}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
